import React, { useEffect, useRef, useState } from "react"
import { Search, X } from "lucide-react"
import { ORDER_FORM_PROGRAMMATIC_SCROLL_ENABLED } from "./orderFormAccordionConstants.js"
import { useOrderFormApi } from "./orderFormApiContext.jsx"

const SEARCH_DEBOUNCE_MS = 350
const RESULTS_LIMIT = 15

const ROW_HOVER_BG = "#f8f9fa"
const ICON_CENTER = { top: "50%", transform: "translateY(-50%)" }

/**
 * Debounced customer search (GET /api/v2/customers).
 * @param {{
 *   idPrefix?: string,
 *   placeholder?: string,
 *   selectedCustomerId?: number | null,
 *   onClearSelection?: () => void,
 *   onSelect: (customer: object) => void,
 *   focusOnMount?: boolean,
 *   clearQueryNonce?: number,
 *   focusNonce?: number,
 *   selectedDisplayLabel?: string | null,
 *   onSearchQueryChange?: (query: string) => void,
 *   disabled?: boolean,
 * }} props
 */
export default function CustomerSearchInput({
    idPrefix = "customer-search",
    placeholder = "Search by name, email, phone, or company...",
    selectedCustomerId = null,
    onClearSelection,
    onSelect,
    focusOnMount = false,
    clearQueryNonce = 0,
    focusNonce = 0,
    selectedDisplayLabel = null,
    onSearchQueryChange,
    disabled = false,
}) {
    const api = useOrderFormApi()
    const containerRef = useRef(/** @type {HTMLDivElement | null} */ (null))
    const searchInputRef = useRef(/** @type {HTMLInputElement | null} */ (null))
    const debounceRef = useRef(/** @type {number | null} */ (null))
    const [searchQuery, setSearchQuery] = useState("")
    const [results, setResults] = useState(/** @type {any[]} */ ([]))
    const [open, setOpen] = useState(false)
    const [loading, setLoading] = useState(false)
    const [searchError, setSearchError] = useState(/** @type {string | null} */ (null))
    const [highlightedIndex, setHighlightedIndex] = useState(0)
    const optionRefs = useRef(/** @type {(HTMLButtonElement | null)[]} */ ([]))
    const suppressSearchDebounceRef = useRef(false)

    function resetSearchInputState() {
        if (debounceRef.current != null) {
            window.clearTimeout(debounceRef.current)
            debounceRef.current = null
        }
        suppressSearchDebounceRef.current = true
        setSearchQuery("")
        setOpen(false)
        setResults([])
        setHighlightedIndex(0)
        setSearchError(null)
        setLoading(false)
    }

    /** Clears typed text, dropdown, and parent selection when a customer was chosen. */
    function clearSearchField() {
        resetSearchInputState()
        if (selectedCustomerId != null) {
            onClearSelection?.()
        }
    }

    useEffect(() => {
        function onDocMouseDown(e) {
            if (containerRef.current && !containerRef.current.contains(e.target)) {
                setOpen(false)
            }
        }
        document.addEventListener("mousedown", onDocMouseDown)
        return () => document.removeEventListener("mousedown", onDocMouseDown)
    }, [])

    /** Parent increments to clear the field (e.g. shipping “Same as contact” rechecked). */
    useEffect(() => {
        if (clearQueryNonce === 0) {
            return
        }
        resetSearchInputState()
    }, [clearQueryNonce])

    /** Parent increments to move focus into the search field (e.g. shipping section opened). */
    useEffect(() => {
        if (focusNonce === 0) {
            return
        }
        searchInputRef.current?.focus({ preventScroll: true })
    }, [focusNonce])

    /** Parent sets the visible query when a customer is chosen programmatically. */
    useEffect(() => {
        if (selectedCustomerId == null) {
            return
        }
        if (selectedDisplayLabel == null || String(selectedDisplayLabel).trim() === "") {
            return
        }
        suppressSearchDebounceRef.current = true
        setSearchQuery(String(selectedDisplayLabel).trim())
    }, [selectedCustomerId, selectedDisplayLabel])

    useEffect(() => {
        onSearchQueryChange?.(searchQuery)
    }, [searchQuery, onSearchQueryChange])

    useEffect(() => {
        if (!focusOnMount) {
            return
        }
        const id = window.setTimeout(() => {
            searchInputRef.current?.focus({ preventScroll: true })
        }, 0)
        return () => window.clearTimeout(id)
    }, [focusOnMount])

    useEffect(() => {
        setHighlightedIndex(0)
    }, [results])

    useEffect(() => {
        if (
            !open ||
            results.length === 0 ||
            !ORDER_FORM_PROGRAMMATIC_SCROLL_ENABLED
        ) {
            return
        }
        const el = optionRefs.current[highlightedIndex]
        el?.scrollIntoView({ block: "nearest" })
    }, [highlightedIndex, open, results.length])

    useEffect(() => {
        if (suppressSearchDebounceRef.current) {
            suppressSearchDebounceRef.current = false
            if (debounceRef.current != null) {
                window.clearTimeout(debounceRef.current)
                debounceRef.current = null
            }
            return
        }
        if (debounceRef.current != null) {
            window.clearTimeout(debounceRef.current)
        }
        const q = searchQuery.trim()
        if (q.length < 1) {
            setResults([])
            setOpen(false)
            setLoading(false)
            setSearchError(null)
            return
        }

        debounceRef.current = window.setTimeout(async () => {
            debounceRef.current = null
            setLoading(true)
            setSearchError(null)
            try {
                const body = await api.getCustomersV2(
                    1,
                    RESULTS_LIMIT,
                    "created_at",
                    "desc",
                    q
                )
                setResults(Array.isArray(body?.data) ? body.data : [])
                setOpen(true)
            } catch (err) {
                setResults([])
                setSearchError(err instanceof Error ? err.message : "Search failed")
                setOpen(true)
            } finally {
                setLoading(false)
            }
        }, SEARCH_DEBOUNCE_MS)

        return () => {
            if (debounceRef.current != null) {
                window.clearTimeout(debounceRef.current)
            }
        }
    }, [searchQuery])

    function handleSearchInputChange(e) {
        const next = e.target.value
        setSearchQuery(next)
        if (selectedCustomerId != null) {
            onClearSelection?.()
        }
    }

    function selectCustomer(c) {
        const name = [c.first_name, c.last_name].filter(Boolean).join(" ").trim()
        suppressSearchDebounceRef.current = true
        setSearchQuery(name || (c.email ?? "") || (c.phone ?? "") || "")
        setOpen(false)
        setResults([])
        setHighlightedIndex(0)
        onSelect(c)
    }

    function handleSearchKeyDown(e) {
        const canPick =
            open && !searchError && results.length > 0 && !loading

        if (e.key === "Escape") {
            if (open && (results.length > 0 || searchError)) {
                e.preventDefault()
                setOpen(false)
            }
            return
        }

        if (!canPick) {
            return
        }

        if (e.key === "Tab" && !e.shiftKey) {
            e.preventDefault()
            const c = results[highlightedIndex]
            if (c) {
                selectCustomer(c)
            }
            return
        }

        if (e.key === "ArrowDown") {
            e.preventDefault()
            setHighlightedIndex((i) => Math.min(results.length - 1, i + 1))
            return
        }
        if (e.key === "ArrowUp") {
            e.preventDefault()
            setHighlightedIndex((i) => Math.max(0, i - 1))
            return
        }
        if (e.key === "Enter") {
            e.preventDefault()
            const c = results[highlightedIndex]
            if (c) {
                selectCustomer(c)
            }
            return
        }
    }

    function formatResultTitle(c) {
        const name = [c.first_name, c.last_name].filter(Boolean).join(" ").trim()
        return name || c.email || c.phone || c.company || "Customer"
    }

    function formatResultSubtitle(c) {
        const parts = [c.email, c.phone].filter(Boolean)
        return parts.join(" · ")
    }

    const listboxId = `${idPrefix}-listbox`
    const showClear = !disabled && searchQuery.trim().length > 0
    const inputPaddingRight =
        showClear && loading ? "4.5rem" : showClear || loading ? "3rem" : undefined

    return (
        <div className="position-relative" ref={containerRef}>
            <Search
                className="position-absolute text-muted"
                size={20}
                style={{ left: "12px", pointerEvents: "none", ...ICON_CENTER }}
            />
            {loading && (
                <span
                    style={{
                        right: showClear ? "38px" : "12px",
                        position: "absolute",
                        ...ICON_CENTER,
                        zIndex: 1,
                    }}
                >
                    <span
                        className="spinner-border spinner-border-sm text-primary"
                        style={{
                            width: "1.1rem",
                            height: "1.1rem",
                            display: "block",
                        }}
                        role="status"
                        aria-hidden
                    />
                </span>
            )}
            {showClear && (
                <button
                    type="button"
                    className="btn btn-link p-0 border-0 position-absolute text-secondary text-decoration-none"
                    style={{
                        right: "10px",
                        zIndex: 2,
                        ...ICON_CENTER,
                    }}
                    aria-label="Clear search"
                    onMouseDown={(e) => {
                        e.preventDefault()
                        e.stopPropagation()
                    }}
                    onClick={() => {
                        clearSearchField()
                        searchInputRef.current?.focus({ preventScroll: true })
                    }}
                >
                    <X size={18} strokeWidth={2} />
                </button>
            )}
            <input
                id={`${idPrefix}-query`}
                ref={searchInputRef}
                type="text"
                autoComplete="off"
                placeholder={placeholder}
                className="form-control ps-5"
                style={{ paddingRight: inputPaddingRight }}
                value={searchQuery}
                disabled={disabled}
                onChange={handleSearchInputChange}
                onKeyDown={handleSearchKeyDown}
                onFocus={() => {
                    if (results.length > 0 || searchError) {
                        setOpen(true)
                    }
                }}
                aria-autocomplete="list"
                aria-expanded={open}
                aria-controls={listboxId}
                aria-activedescendant={
                    open && results.length > 0
                        ? `${idPrefix}-option-${highlightedIndex}`
                        : undefined
                }
            />
            {open && (
                <div
                    id={listboxId}
                    className="position-absolute w-100 mt-1 bg-white border rounded shadow"
                    style={{
                        zIndex: 1050,
                        maxHeight: "240px",
                        overflowY: "auto",
                    }}
                    role="listbox"
                >
                    {searchError && (
                        <div className="p-3 small text-danger">{searchError}</div>
                    )}
                    {!searchError &&
                        !loading &&
                        searchQuery.trim().length >= 1 &&
                        results.length === 0 && (
                            <div className="p-3 small text-muted">No customers found.</div>
                        )}
                    {!searchError &&
                        results.map((c, index) => (
                            <button
                                key={String(c.id)}
                                id={`${idPrefix}-option-${index}`}
                                ref={(el) => {
                                    optionRefs.current[index] = el
                                }}
                                type="button"
                                tabIndex={-1}
                                role="option"
                                aria-selected={highlightedIndex === index}
                                className="w-100 text-start p-3 border-bottom btn btn-link text-decoration-none"
                                style={{
                                    color: "inherit",
                                    cursor: "pointer",
                                    borderRadius: 0,
                                    backgroundColor:
                                        highlightedIndex === index
                                            ? ROW_HOVER_BG
                                            : "white",
                                }}
                                onMouseDown={(e) => e.preventDefault()}
                                onMouseEnter={() => setHighlightedIndex(index)}
                                onClick={() => selectCustomer(c)}
                            >
                                <div className="fw-semibold text-dark">
                                    {formatResultTitle(c)}
                                </div>
                                {formatResultSubtitle(c) && (
                                    <div className="small text-muted">
                                        {formatResultSubtitle(c)}
                                    </div>
                                )}
                                {c.company && (
                                    <div className="small text-secondary">{c.company}</div>
                                )}
                            </button>
                        ))}
                </div>
            )}
        </div>
    )
}
